/**
 * Authentication Service - Refactored with Unified Architecture
 * Migrated from axios to fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { IAuthForm, ILoginForm, AuthUser } from "@/types/auth.types";
import { AnyRole } from "@/enums/auth.service";
import { useAuthStore } from "@/store/auth.store";

import { BaseService } from "./core/base-service";
import { httpClient, httpClientProxy } from "./core/http-client";

// Enhanced type definitions for auth responses
export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
  message?: string;
}

export interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ForgotPasswordResponse {
  message: string;
  email?: string;
  phone?: string;
}

export interface ResetPasswordResponse {
  message: string;
  success: boolean;
}

// JWT Payload interface matching backend
interface JWTPayload {
  sub: string;           // User ID
  email: string;         // User email
  role: string;          // Single role (primary system)
  iat: number;
  exp: number;
}

/**
 * Utility functions for JWT token handling
 */
const JWTUtils = {
  /**
   * Decode JWT token payload without verification (for client-side role extraction)
   */
  decodePayload(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as JWTPayload;
    } catch (error) {
      console.error('Failed to decode JWT payload:', error);
      return null;
    }
  },

  /**
   * Extract user role from JWT payload
   */
  extractRole(payload: JWTPayload): AnyRole | null {
    // Extract single role from JWT payload
    if (payload.role) {
      return payload.role as AnyRole;
    }
    
    return null;
  },

  /**
   * Check if token is expired
   */
  isTokenExpired(payload: JWTPayload): boolean {
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }
};

/**
 * Authentication Service Class
 * Handles all authentication-related operations
 * Single Responsibility: Authentication and token management
 */
class AuthService extends BaseService {
  constructor() {
    super({
      name: "auth",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 2, // Auth operations should not retry too many times
      retryDelay: 1000,
    });
  }

  /**
   * Main authentication method for login/register (EXACT API match)
   * This matches the original authService.main() method exactly
   */
  async main(type: "login" | "register", data: IAuthForm | ILoginForm) {
    try {
      const response = await this.client.post(`/auth/${type}`, data);
      return response; // Return full response object like original
    } catch (error) {
      throw error; // Throw error directly like original
    }
  }

  /**
   * Refresh authentication tokens with role extraction
   * Updated to extract and update user role from new tokens
   */
  async getNewTokens() {
    try {
      const apiUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000";
      const res = await fetch(`${apiUrl}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to refresh tokens: ${res.status}`);
      }

      const tokenResponse = await res.json();
      
      // NEW: Extract role from new access token and update store
      if (tokenResponse.access_token) {
        this.updateUserRoleFromToken(tokenResponse.access_token);
      }

      return tokenResponse;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Google OAuth authentication (EXACT API match)
   */
  async loginGoogle(token: string) {
    try {
      const response = await this.client.post(`/auth/google`, {
        access_token: token,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout user (EXACT API match)
   */
  async logout() {
    try {
      const response = await this.client.post("/auth/logout");
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request password reset via email (EXACT API match)
   */
  async forgotPasswordFromEmail(email: string) {
    return this.client.post("/auth/forgot-password/email", { email });
  }

  /**
   * Request password reset via phone (EXACT API match)
   */
  async forgotPasswordFromPhone(phone: string) {
    return this.client.post("/auth/forgot-password/phone", { phone });
  }

  /**
   * Reset password using email token (EXACT API match)
   */
  async resetPasswordEmail(token: string, password: string) {
    // Backend expects token as query parameter and password in body
    return this.client.patch(
      `/auth/reset-password/email?token=${encodeURIComponent(token)}`,
      {
        password,
      },
    );
  }

  /**
   * NEW: Extract and update user role from JWT token
   * Used after token refresh to sync role changes
   */
  private updateUserRoleFromToken(accessToken: string): void {
    try {
      const payload = JWTUtils.decodePayload(accessToken);
      if (!payload) {
        this.logger.warn("Failed to decode access token for role extraction");
        return;
      }

      const role = JWTUtils.extractRole(payload);
      if (role) {
        // Update role in auth store
        const { updateRole } = useAuthStore.getState();
        updateRole(role);
        
        this.logger.info("User role updated from token", { role });
      } else {
        this.logger.warn("No role found in JWT payload", { payload });
      }
    } catch (error) {
      this.logger.error("Error updating role from token", { error });
    }
  }

  /**
   * NEW: Extract user role from current stored tokens
   * Useful for checking role without making API calls
   */
  getCurrentUserRole(): AnyRole | null {
    try {
      // Try to get token from cookies or localStorage
      const token = this.getStoredAccessToken();
      if (!token) return null;

      const payload = JWTUtils.decodePayload(token);
      if (!payload) return null;

      return JWTUtils.extractRole(payload);
    } catch (error) {
      this.logger.debug("Error extracting role from stored token", { error });
      return null;
    }
  }

  /**
   * NEW: Check if current user has specific role (with hierarchy support)
   */
  async hasRole(role: AnyRole): Promise<boolean> {
    const currentRole = this.getCurrentUserRole();
    if (!currentRole) return false;

    // Import role hierarchy from enums
    const { ROLE_HIERARCHY } = await import("@/enums/auth.service");

    // Check direct role match
    if (currentRole === role) return true;

    // Check role hierarchy - higher roles can access lower role functionality
    const allowedRoles = ROLE_HIERARCHY[currentRole] || [];
    return allowedRoles.includes(role);
  }

  /**
   * NEW: Get user permissions based on role
   */
  async getUserPermissions(): Promise<string[]> {
    const role = this.getCurrentUserRole();
    if (!role) return [];

    // Import role enums dynamically
    const { AdminRole, UserRole } = await import("@/enums/auth.service");

    // Define permissions based on role
    const permissionMap: Record<AnyRole, string[]> = {
      // Admin roles
      [AdminRole.SUPER_ADMIN]: ['*'], // All permissions
      [AdminRole.ADMIN]: ['property.create', 'property.edit', 'property.delete', 'user.manage', 'developer.manage'],
      [AdminRole.MAIN_REALTOR]: ['property.create', 'property.edit', 'user.view', 'lead.manage'],
      [AdminRole.REALTOR]: ['property.create', 'property.edit', 'lead.manage'],
      [AdminRole.SECRETARY]: ['property.view', 'lead.view'],
      
      // User roles
      [UserRole.USER]: ['property.view', 'profile.edit'],
      [UserRole.OWNER]: ['property.view', 'property.create', 'profile.edit'],
      [UserRole.GUEST]: ['property.view'],
      [UserRole.USER_REALTOR]: ['property.view', 'property.create', 'profile.edit', 'lead.create']
    };

    return permissionMap[role] || [];
  }

  /**
   * NEW: Check if user has specific permission
   */
  async hasPermission(permission: string): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    
    // Super admin has all permissions
    if (permissions.includes('*')) return true;
    
    return permissions.includes(permission);
  }

  /**
   * NEW: Helper method to get stored access token
   * Checks cookies and localStorage for token
   */
  private getStoredAccessToken(): string | null {
    // Implementation depends on where tokens are stored
    // This is a placeholder for the actual token retrieval logic
    try {
      // Check cookies first (most secure)
      if (typeof document !== 'undefined') {
        const cookies = document.cookie.split(';');
        const accessTokenCookie = cookies.find(cookie => 
          cookie.trim().startsWith('access_token=')
        );
        if (accessTokenCookie) {
          return accessTokenCookie.split('=')[1];
        }
      }

      // Fallback to localStorage if needed (less secure)
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem('access_token');
      }
    } catch (error) {
      this.logger.debug("Error retrieving stored token", { error });
    }
    
    return null;
  }

  /**
   * Check if user is authenticated
   * Updated to use avezor-api endpoint
   */
  async checkAuthStatus(): Promise<{ authenticated: boolean; user?: any }> {
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
      const response = await httpClientProxy.get<{
        authenticated: boolean;
        user?: any;
      }>(`${apiUrl}/api/auth/status`, {
        credentials: "include",
        cache: "no-store",
      });

      return response.data;
    } catch (error) {
      // Non-authenticated is not an error, just return false
      this.logger.debug("Auth status check failed, user not authenticated");
      return { authenticated: false };
    }
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  private isValidPassword(password: string): boolean {
    // At least 8 characters, one letter, one number
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    return passwordRegex.test(password);
  }

  /**
   * Enhanced validation for auth data
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // First run base validation
    super.validateRequired(params, requiredFields);

    // Additional auth-specific validation
    if (params.email && !this.isValidEmail(params.email)) {
      this.handleError(
        {
          message: "Invalid email format",
          code: "INVALID_EMAIL",
          details: { email: params.email },
        },
        "email validation",
      );
    }

    if (params.password && !this.isValidPassword(params.password)) {
      this.handleError(
        {
          message:
            "Password must be at least 8 characters with letters and numbers",
          code: "INVALID_PASSWORD",
        },
        "password validation",
      );
    }
  }

  /**
   * Custom health check for auth service
   */
  async healthCheck() {
    try {
      // Check if auth endpoints are responding
      await this.checkAuthStatus();

      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          status: "healthy",
          refresh: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export for backward compatibility
export default authService;

// Export JWT utilities for external use
export { JWTUtils };

// Export types for external use
export type {
  AuthResponse,
  TokenRefreshResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  JWTPayload,
};
